import React, { useRef, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { tiltSpring } from '../../lib/fluid-animations';

interface TiltCardProps {
  children: React.ReactNode;
  className?: string;
  tiltAmount?: number; // Max tilt in degrees
  scaleOnHover?: number;
  glareEnabled?: boolean;
}

/**
 * TiltCard - 3D perspective tilt card with shine overlay
 *
 * Features:
 * - Perspective tilt based on mouse position (rotateX/Y)
 * - Three depth layers: base, content (translateZ), shine
 * - Spring physics for smooth return to neutral
 */
const TiltCard: React.FC<TiltCardProps> = ({
  children,
  className = '',
  tiltAmount = 10,
  scaleOnHover = 1.02,
  glareEnabled = true
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();

  // Mouse position relative to card center (-0.5 to 0.5)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  // Spring-animated values
  const rotateX = useSpring(mousePos.y * -tiltAmount, tiltSpring);
  const rotateY = useSpring(mousePos.x * tiltAmount, tiltSpring);
  const scale = useSpring(isHovered ? scaleOnHover : 1, tiltSpring);

  // Glare position (moves opposite to mouse)
  const glareX = useTransform(rotateY, [-tiltAmount, tiltAmount], ['30%', '70%']);
  const glareY = useTransform(rotateX, [-tiltAmount, tiltAmount], ['70%', '30%']);
  const glareOpacity = useSpring(isHovered ? 0.15 : 0, tiltSpring);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current || prefersReducedMotion) return;

    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;

    setMousePos({ x, y });
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setMousePos({ x: 0, y: 0 });
  };

  if (prefersReducedMotion) {
    return (
      <div className={className}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        rotateX,
        rotateY,
        scale,
        transformStyle: 'preserve-3d',
        perspective: '1000px'
      }}
      className={`relative ${className}`}
    >
      {/* Paper texture background layer */}
      <div
        className="absolute inset-0 rounded-inherit bg-white"
        style={{ transform: 'translateZ(-20px)' }}
      />

      {/* Main content layer - floats above base */}
      <div
        className="relative"
        style={{ transform: 'translateZ(40px)' }}
      >
        {children}
      </div>

      {/* Glare/shine overlay */}
      {glareEnabled && (
        <motion.div
          className="absolute inset-0 pointer-events-none rounded-inherit overflow-hidden"
          style={{ transform: 'translateZ(60px)' }}
        >
          <motion.div
            className="absolute w-[200%] h-[200%]"
            style={{
              background: 'radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 50%)',
              left: glareX,
              top: glareY,
              x: '-50%',
              y: '-50%',
              opacity: glareOpacity
            }}
          />
        </motion.div>
      )}
    </motion.div>
  );
};

/**
 * TiltCardSimple - Lightweight version without depth layers
 * Good for smaller cards or when full 3D effect is too much
 */
export const TiltCardSimple: React.FC<TiltCardProps> = ({
  children,
  className = '',
  tiltAmount = 5,
  scaleOnHover = 1.01
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const rotateX = useSpring(mousePos.y * -tiltAmount, tiltSpring);
  const rotateY = useSpring(mousePos.x * tiltAmount, tiltSpring);
  const scale = useSpring(isHovered ? scaleOnHover : 1, tiltSpring);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!ref.current || prefersReducedMotion) return;
    const rect = ref.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setMousePos({ x, y });
  };

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setMousePos({ x: 0, y: 0 });
      }}
      style={{ rotateX, rotateY, scale }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default TiltCard;
